import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect } from "react-redux";
import {
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import "./WSGRSteps.scss";
import FallbackTickerIcon from "../../../common/FallbackTickerIcon/FallbackTickerIcon";
import moment from "moment";
import { portfoliosData } from "../../portfoliosData";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import { wealthGuidedRebalancesDelete } from "../../../../appRedux/ducks/wealthGuidedRebalancesDelete";
import { wealthGuidedRebalancesSubmit } from "../../../../appRedux/ducks/wealthGuidedRebalances";
import { wealthGuidedRebalancesGet } from "../../../../appRedux/ducks/wealthGuidedRebalancesGet";
import ArrowRightAltRoundedIcon from "@mui/icons-material/ArrowRightAltRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const WSGRStep3 = ({
  themeMode,
  setStep,
  selectedPortfolio,
  selectedDate,
  tier,
  wealthGuidedRebalancesGet,
  deleteGuidedRebalances,
  submitGuidedRebalance,
  fetchGuidedRebalances,
  wealthGuidedRebalances,
  wealthRecentRebalances,
  onExitToPortfolioActivity,
  onDirectNavigation,
}) => {
  // Use Redux loading state instead of local state
  const isLoading = wealthGuidedRebalancesGet?.loading?.[tier] || false;
  const isSubmitting = wealthGuidedRebalances?.loading?.[tier] || false;

  // AUM values by tier
  const getAumOptions = (tier) => {
    if (tier === "core") {
      return [
        30000, 40000, 50000, 60000, 70000, 90000, 100000, 125000, 150000,
        175000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 550000,
        600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000,
      ];
    }
    if (tier === "atlas") {
      return [
        50000, 60000, 70000, 80000, 90000, 100000, 125000, 150000, 175000,
        200000, 250000, 300000, 350000, 400000, 450000, 500000, 550000, 600000,
        650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000,
      ];
    }
    if (tier === "titan") {
      return [
        100000, 125000, 150000, 175000, 200000, 225000, 250000, 275000, 300000,
        350000, 400000, 450000, 500000, 550000, 600000, 650000, 700000, 750000,
        800000, 850000, 900000, 950000, 1000000,
      ];
    }
    if (tier === "everest") {
      return [
        100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000,
        550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000,
        1000000, 1100000, 1200000, 1300000, 1400000, 1500000, 1600000, 1700000,
        1800000, 1900000, 2000000,
      ];
    }
    return [50000, 100000, 200000, 500000, 1000000];
  };

  const getDefaultAum = (tier) => {
    if (tier === "core") return 30000;
    if (tier === "atlas") return 50000;
    if (tier === "titan") return 100000;
    if (tier === "everest") return 100000;
    return 50000;
  };

  const aumOptions = useMemo(() => getAumOptions(tier), [tier]);
  const [selectedAum, setSelectedAum] = useState(() => getDefaultAum(tier));
  const [hasInitializedAum, setHasInitializedAum] = useState(false);
  const previousIsSubmittingRef = useRef(false);

  // Sorting state - column and direction
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: "asc",
  });

  const guidedRebalancesData =
    wealthGuidedRebalancesGet?.guidedRebalancesData?.[tier];

  // Handle column header clicks for sorting
  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.column === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ column, direction });
  };

  // Render sort indicator for column headers
  const renderSortIndicator = (column) => {
    if (sortConfig.column !== column) {
      return null;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 14, marginLeft: "4px" }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14, marginLeft: "4px" }} />
    );
  };

  // Process the guided rebalances data into table format
  const rebalanceItems = useMemo(() => {
    if (
      !guidedRebalancesData ||
      !Array.isArray(guidedRebalancesData) ||
      guidedRebalancesData.length === 0
    ) {
      return [];
    }

    const rebalanceData = guidedRebalancesData[0]; // Get first (and likely only) item
    if (!rebalanceData.assets) {
      return [];
    }

    // Convert assets to table format
    let items = rebalanceData.assets.map((asset) => ({
      ticker: asset.ticker,
      sector: asset.sector,
      currentQuantity: asset.current_quantity,
      newQuantity: asset.new_quantity,
      change: asset.quantity_diff,
    }));

    // Apply user sorting if configured
    if (sortConfig.column) {
      items = items.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.column) {
          case "ticker":
            aValue = a.ticker.toLowerCase();
            bValue = b.ticker.toLowerCase();
            break;
          case "currentQuantity":
            aValue = a.currentQuantity;
            bValue = b.currentQuantity;
            break;
          case "newQuantity":
            aValue = a.newQuantity;
            bValue = b.newQuantity;
            break;
          case "change":
            aValue = a.change;
            bValue = b.change;
            break;
          default:
            return 0;
        }

        if (sortConfig.direction === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    } else {
      // Default sorting by adjustment sequence priority when no user sorting is applied
      items = items.sort((a, b) => {
        // Adjustment Sequence Priority:
        // 1. Tickers to sell (new qty becomes 0)
        // 2. Tickers to adjust down (decrease in qty)
        // 3. Tickers to adjust up (increase in qty)
        // 4. Tickers to buy (current qty is 0 and new qty is non-zero)

        const getAdjustmentPriority = (item) => {
          if (item.currentQuantity > 0 && item.newQuantity === 0) return 1; // Sell
          if (item.change < 0) return 2; // Adjust down
          if (item.change > 0 && item.currentQuantity > 0) return 3; // Adjust up
          if (item.currentQuantity === 0 && item.newQuantity > 0) return 4; // Buy
          return 5; // No change (shouldn't happen, but just in case)
        };

        const priorityA = getAdjustmentPriority(a);
        const priorityB = getAdjustmentPriority(b);

        // First sort by priority
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Within same priority, sort by largest absolute change first
        return Math.abs(b.change) - Math.abs(a.change);
      });
    }

    return items;
  }, [guidedRebalancesData, sortConfig]);

  // Create portfolios mapping that includes both regular and auto portfolio IDs
  const portfolios = useMemo(() => {
    const portfolioMap = {};

    portfoliosData[tier]?.portfolios?.forEach((portfolio) => {
      // Add regular portfolio mapping
      portfolioMap[portfolio.id] = portfolio.name;
      // Add auto portfolio mapping if it exists
      if (portfolio.autoId) {
        portfolioMap[portfolio.autoId] = portfolio.name;
      }
    });

    return portfolioMap;
  }, [tier]);

  // Get AUM allocation from the response data (for initial load)
  const aumAllocation = useMemo(() => {
    if (
      !guidedRebalancesData ||
      !Array.isArray(guidedRebalancesData) ||
      guidedRebalancesData.length === 0
    ) {
      return null;
    }
    return guidedRebalancesData[0]?.aum_allocation;
  }, [guidedRebalancesData]);

  // Initialize selectedAum from API response if available (only once)
  useEffect(() => {
    if (aumAllocation && !hasInitializedAum) {
      setSelectedAum(aumAllocation);
      setHasInitializedAum(true);
    }
  }, [aumAllocation, hasInitializedAum]);

  // Find the rebalance_id from the selected date and portfolio
  const selectedRebalanceId = useMemo(() => {
    if (
      !selectedPortfolio ||
      !selectedDate ||
      !wealthRecentRebalances?.recentRebalances?.[tier]
    ) {
      return null;
    }

    const portfolioRebalances =
      wealthRecentRebalances.recentRebalances[tier][selectedPortfolio];

    if (!portfolioRebalances || !Array.isArray(portfolioRebalances)) {
      return null;
    }

    // Find the rebalance that matches our selected date
    const matchingRebalance = portfolioRebalances.find(
      (item) =>
        moment(item.rebalance_date).format("YYYY-MM-DD") === selectedDate,
    );

    return matchingRebalance?.rebalance_id || null;
  }, [selectedPortfolio, selectedDate, wealthRecentRebalances, tier]);

  // Handle AUM change - auto-refetch
  const handleAumChange = (event) => {
    const newAum = event.target.value;
    setSelectedAum(newAum);

    // Auto-submit with new AUM
    if (selectedRebalanceId && selectedPortfolio) {
      const payload = {
        portfolio_id: selectedPortfolio,
        rebalance_id: selectedRebalanceId,
        aum_allocation: newAum,
      };
      submitGuidedRebalance(tier, payload);
    }
  };

  // Refetch data after successful AUM update (PUT succeeds)
  useEffect(() => {
    // Watch for when PUT completes (isSubmitting transitions from true to false)
    const wasSubmitting = previousIsSubmittingRef.current;
    const justCompleted = wasSubmitting && !isSubmitting;

    if (
      justCompleted &&
      wealthGuidedRebalances?.guidedRebalances?.[tier] &&
      selectedRebalanceId &&
      selectedPortfolio
    ) {
      // PUT was successful, now fetch the updated guided rebalances data
      fetchGuidedRebalances(tier);
    }

    // Update ref for next render
    previousIsSubmittingRef.current = isSubmitting;
  }, [
    isSubmitting,
    wealthGuidedRebalances,
    tier,
    selectedRebalanceId,
    selectedPortfolio,
    fetchGuidedRebalances,
  ]);

  // Get UUID from the response data
  const guidedRebalanceUuid = useMemo(() => {
    if (
      !guidedRebalancesData ||
      !Array.isArray(guidedRebalancesData) ||
      guidedRebalancesData.length === 0
    ) {
      return null;
    }
    return guidedRebalancesData[0]?.uuid;
  }, [guidedRebalancesData]);

  // Handle going back to portfolio selection - navigate immediately and send DELETE in background
  const handleBackToPortfolioSelection = () => {
    if (onDirectNavigation) {
      // Use the parent's direct navigation handler which handles background DELETE
      onDirectNavigation(1, guidedRebalanceUuid);
    } else {
      // Fallback to direct navigation if handler not available
      setStep(1);
      if (guidedRebalanceUuid) {
        deleteGuidedRebalances(guidedRebalanceUuid);
      }
    }
  };

  // Handle X button click - go directly to portfolio activity (no DELETE)
  const handleClose = () => {
    onExitToPortfolioActivity();
  };

  // Handle restarting the guide - navigate immediately and send DELETE in background
  const handleRestartGuide = () => {
    if (onDirectNavigation) {
      // Use the parent's direct navigation handler which handles background DELETE
      onDirectNavigation(0, guidedRebalanceUuid);
    } else {
      // Fallback to direct navigation if handler not available
      setStep(0);
      if (guidedRebalanceUuid) {
        deleteGuidedRebalances(guidedRebalanceUuid);
      }
    }
  };

  /* Add scroll logic for horizontal scrolling .ws-gr-step-3-accordion-tableheader and .ws-gr-step-3-accordion-tablebody together */
  useEffect(() => {
    const tableHeader = document.querySelector(
      ".ws-gr-step-3-accordion-tableheader",
    );
    const tableBody = document.querySelector(
      ".ws-gr-step-3-accordion-tablebody",
    );

    if (!tableHeader || !tableBody) return;

    const syncHeaderScroll = () => {
      tableBody.scrollLeft = tableHeader.scrollLeft;
    };
    const syncBodyScroll = () => {
      tableHeader.scrollLeft = tableBody.scrollLeft;
    };

    tableHeader.addEventListener("scroll", syncHeaderScroll);
    tableBody.addEventListener("scroll", syncBodyScroll);

    return () => {
      tableHeader.removeEventListener("scroll", syncHeaderScroll);
      tableBody.removeEventListener("scroll", syncBodyScroll);
    };
  }, []);

  return (
    <div className="ws-gr-step-3">
      <div className="ws-gr-step-counter-mobile">Step 2 of 2</div>

      <div className="ws-gr-step-3-content">
        <div className="ws-gr-step-3-content-header">
          <div className="ws-gr-step-3-header-with-close">
            <div className="ws-gr-step-3-content-header-title">
              Rebalance Calculation Summary
            </div>
            <IconButton
              className="ws-gr-close-button"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="ws-gr-step-3-content-header-description">
            For {portfolios[selectedPortfolio]} in{" "}
            {tier.charAt(0).toUpperCase() + tier.slice(1)}, from last adjusted
            date{" "}
            {selectedDate
              ? moment(selectedDate).format("MMM DD, YYYY")
              : "Nov 24, 2024"}
            , with a model AUM of{" "}
            <FormControl
              size="small"
              sx={{
                minWidth: 120,
                display: "inline-block",
                verticalAlign: "middle",
                marginLeft: "4px",
              }}
            >
              <Select
                value={selectedAum}
                onChange={handleAumChange}
                disabled={isSubmitting || !selectedRebalanceId}
                sx={{
                  fontSize: "16px",
                  height: "32px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      themeMode === "light"
                        ? "rgba(0, 0, 0, 0.23)"
                        : "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      themeMode === "light"
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(255, 255, 255, 0.87)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: themeMode === "light" ? "#007BFF" : "#009EFF",
                  },
                }}
              >
                {aumOptions.map((aum) => (
                  <MenuItem key={aum} value={aum}>
                    ${Number(aum).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className="ws-gr-step-3-content-accordions">
          <div className="ws-gr-step-3-content-accordion">
            <div className={`ws-gr-step-3-content-accordion-body open`}>
              <div className="ws-gr-step-3-accordion-tableheader">
                <div
                  className="ws-gr-step-3-accordion-cell sortable-header"
                  onClick={() => handleSort("ticker")}
                >
                  <span className="flex items-center">
                    Tickers
                    {renderSortIndicator("ticker")}
                  </span>
                </div>
                <div className="ws-gr-step-3-accordion-cell sector-info">
                  Sector
                </div>
                <div className="ws-gr-step-3-accordion-cell text-center combined-quantities">
                  <span
                    className="flex items-center gap-1 sortable-header"
                    onClick={() => handleSort("currentQuantity")}
                  >
                    <Tooltip
                      title="The number of shares currently held before this rebalance, based on current stock prices."
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 16, cursor: "pointer" }}
                      />
                    </Tooltip>
                    Current Quantity
                    {renderSortIndicator("currentQuantity")}
                  </span>
                  <span
                    className="flex items-center gap-1 sortable-header"
                    onClick={() => handleSort("newQuantity")}
                  >
                    New Quantity
                    {renderSortIndicator("newQuantity")}
                    <Tooltip
                      title="The desired number of shares to hold after the rebalance, based on current stock prices."
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 16, cursor: "pointer" }}
                      />
                    </Tooltip>
                  </span>
                </div>
                <div className="ws-gr-step-3-accordion-cell text-right quantity-change-label">
                  <span
                    className="flex items-center gap-1 justify-end sortable-header"
                    onClick={() => handleSort("change")}
                  >
                    <Tooltip
                      title="The number of shares to buy or sell to reach the New Quantity, assuming the current holdings are accurate."
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 16, cursor: "pointer" }}
                      />
                    </Tooltip>
                    Quantity to Change
                    {renderSortIndicator("change")}
                  </span>
                </div>
              </div>

              <div className="ws-gr-step-3-accordion-tablebody">
                {isLoading ? (
                  // Show skeleton loading while data is being fetched
                  Array.from({ length: 10 }, (_, index) => (
                    <div className="ws-gr-step-3-accordion-row" key={index}>
                      <div className="ws-gr-step-3-accordion-data-cell ticker-info">
                        <BeautifulSkeleton
                          variant="circular"
                          width={24}
                          height={24}
                        />
                        <BeautifulSkeleton
                          variant="text"
                          width={60}
                          height={24}
                        />
                      </div>
                      <div className="ws-gr-step-3-accordion-data-cell sector-info">
                        <BeautifulSkeleton
                          variant="text"
                          width={200}
                          height={24}
                        />
                      </div>
                      <div className="ws-gr-step-3-accordion-data-cell text-center combined-quantities-data">
                        <BeautifulSkeleton
                          variant="text"
                          width={16}
                          height={24}
                        />
                        <ArrowRightAltRoundedIcon
                          sx={{
                            color: Math.random() < 0.5 ? "#2E7D32" : "#D32F2F",
                          }}
                        />
                        <BeautifulSkeleton
                          variant="text"
                          width={16}
                          height={24}
                        />
                      </div>
                      <div className="ws-gr-step-3-accordion-data-cell quantity-change-container">
                        <BeautifulSkeleton
                          variant="text"
                          width={35}
                          height={24}
                        />
                      </div>
                    </div>
                  ))
                ) : rebalanceItems.length > 0 ? (
                  rebalanceItems.map((item, index) => (
                    <div className="ws-gr-step-3-accordion-row" key={index}>
                      <div className="ws-gr-step-3-accordion-data-cell ticker-info">
                        <FallbackTickerIcon
                          src={`https://assets.tradealgo.com/stock-logos/${item.ticker}.png`}
                          ticker={item.ticker}
                          className="ws-gr-step-3-accordion-data-cell-ticker-icon"
                        />
                        {item.ticker}
                      </div>
                      <div className="ws-gr-step-3-accordion-data-cell sector-info">
                        {item.sector || "N/A"}
                      </div>
                      <div className="ws-gr-step-3-accordion-data-cell text-center combined-quantities-data">
                        <span>{item.currentQuantity.toLocaleString()}</span>
                        <ArrowRightAltRoundedIcon
                          sx={{
                            color:
                              item.newQuantity === item.currentQuantity
                                ? themeMode === "light"
                                  ? "#3C3C3F"
                                  : "#F8FAFC"
                                : item.newQuantity >= item.currentQuantity
                                  ? themeMode === "light"
                                    ? "#2E7D32"
                                    : "#66BB6A"
                                  : themeMode === "light"
                                    ? "#D32F2F"
                                    : "#F44336",
                          }}
                        />
                        <span>{item.newQuantity.toLocaleString()}</span>
                      </div>
                      <div className="ws-gr-step-3-accordion-data-cell quantity-change-container">
                        <div className="quantity-change-chip">
                          {item.change === 0
                            ? "0"
                            : item.change < 0
                              ? `− ${Math.abs(item.change).toLocaleString()}`
                              : `+ ${item.change.toLocaleString()}`}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="ws-gr-step-3-accordion-row">
                    <div
                      className="ws-gr-step-3-accordion-data-cell"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        gridColumn: "1 / -1",
                      }}
                    >
                      No rebalance data available
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ws-gr-step-footer">
        <div className="ws-gr-step-indicator">Step 2 of 2</div>
        <div className="ws-gr-step-footer-buttons">
          <Button
            className="ws-gr-step-footer-back-button"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToPortfolioSelection}
          >
            Back to Portfolio Selection
          </Button>
          <Button
            className="ws-gr-step-footer-next-button"
            startIcon={<RestartAltIcon />}
            onClick={handleRestartGuide}
          >
            Restart guide
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  wealthGuidedRebalancesGet: state.wealthGuidedRebalancesGet,
  wealthGuidedRebalances: state.wealthGuidedRebalances,
  wealthRecentRebalances: state.wealthRecentRebalances,
});

const mapDispatchToProps = (dispatch) => ({
  deleteGuidedRebalances: (uuid) =>
    dispatch(wealthGuidedRebalancesDelete(uuid)),
  submitGuidedRebalance: (tier, payload) =>
    dispatch(wealthGuidedRebalancesSubmit(tier, payload)),
  fetchGuidedRebalances: (tier) => dispatch(wealthGuidedRebalancesGet(tier)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WSGRStep3);
